// client/src/components/auth/ModernProfile.js
import React, { useState, useEffect, useCallback } from 'react'
import useAuth from '../../hooks/useAuth'
import ApiUsers from '../../api/ApiUsers'
import ApiPlayers from '../../api/ApiPlayers'
import toastr from 'toastr'
import PageLayout from '../common/layout/PageLayout'
import addRipple from '../../utils/ripple'
import ProfileHeader from './profile/ProfileHeader'
import TennisProfileCard from './profile/TennisProfileCard'
import ContactInfoCard from './profile/ContactInfoCard'
import EmailPreferencesCard from './profile/EmailPreferencesCard'
import AboutSection from './profile/AboutSection'
import featureFlags from '../../config/featureFlags'
import '../../styles/profileFields.css'
import DefaultAvatar from '../../images/AnonPersonIcon.png'

function calculateAgeFromBirthdate(birthdate) {
  if (!birthdate) return null
  const birth = new Date(birthdate)
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const m = today.getMonth() - birth.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--
  }
  return age
}

function ModernProfile() {
  const { user, loading } = useAuth()

  // DEBUG: Log feature flags on mount
  useEffect(() => {
    console.log('🚩 Feature Flags Debug:', {
      SMS_NOTIFICATIONS: featureFlags.SMS_NOTIFICATIONS,
      REQUIRE_AVAILABILITY_FOR_PROPOSALS:
        featureFlags.REQUIRE_AVAILABILITY_FOR_PROPOSALS,
      allFlags: featureFlags,
    })
  }, [])
  const [userData, setUserData] = useState(null)
  const [originalUserData, setOriginalUserData] = useState(null)
  const [playerData, setPlayerData] = useState(null)
  const [originalPlayerData, setOriginalPlayerData] = useState(null)
  const [editingSection, setEditingSection] = useState(null) // 'header' | 'tennis' | 'contact' | 'about' | 'email' | null
  const [editingField, setEditingField] = useState(null)
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [avatarUrl, setAvatarUrl] = useState(null)
  const [avatarFile, setAvatarFile] = useState(null)
  const [avatarError, setAvatarError] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordLoading, setPasswordLoading] = useState(false)

  const loadPrimaryPlayer = useCallback(async () => {
    try {
      const players = await ApiPlayers.getMyPlayers()
      const primary = players.find((p) => p.primary_player)
      if (primary) {
        const birthdate = primary.birthdate || '2000-01-01'
        const withBirth = { ...primary, birthdate }
        setPlayerData(withBirth)
        setOriginalPlayerData(withBirth)
      } else {
        // Auto-create a primary player if missing (likely social sign-up)
        if (user) {
          const first = user.firstname || ''
          const last = user.lastname || ''
          const newId = await ApiPlayers.create({
            user_id: user.id,
            first_name: first,
            last_name: last,
            primary_player: true,
          })
          const created = await ApiPlayers.getById(newId)
          const birthdate = created.birthdate || '2000-01-01'
          const withBirth = { ...created, birthdate }
          setPlayerData(withBirth)
          setOriginalPlayerData(withBirth)
        } else {
          setPlayerData(null)
          setOriginalPlayerData(null)
        }
      }
    } catch (err) {
      toastr.error('Failed to load player info.')
      console.error(err)
      setPlayerData(null) // Ensure state is null on error
      setOriginalPlayerData(null)
    }
  }, [user]) // depend on user so we can create player

  useEffect(() => {
    if (user) {
      // Ensure we have sensible defaults
      const safeUser = {
        ...user,
        email: user.email || '',
        firstname: user.firstname || '',
        lastname: user.lastname || '',
        weekly_email_opt_in: user.weekly_email_opt_in ?? true,
      }
      setUserData(safeUser)
      setOriginalUserData(safeUser)
      // Support both `avatar` (current DB column) and legacy `avatar_url`
      setAvatarUrl(safeUser.avatar || safeUser.avatar_url || null)
      setAvatarError(false) // Reset error state when user data loads
      loadPrimaryPlayer()
    }
  }, [user, loadPrimaryPlayer])

  const handleUserDataChange = (changedUserDataFields) => {
    setUserData((prevUserData) => ({
      ...prevUserData,
      ...changedUserDataFields,
    }))
    if (
      playerData &&
      typeof changedUserDataFields.firstname !== 'undefined' &&
      typeof changedUserDataFields.lastname !== 'undefined'
    ) {
      setPlayerData((prevPlayerData) => ({
        ...prevPlayerData,
        first_name: changedUserDataFields.firstname,
        last_name: changedUserDataFields.lastname,
      }))
    }
  }

  // Avatar upload handler
  const handleAvatarChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Validate file size (5MB limit to match backend)
      if (file.size > 5 * 1024 * 1024) {
        toastr.error('File size too large. Maximum size is 5MB.')
        return
      }
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
      if (!allowedTypes.includes(file.type)) {
        toastr.error(
          'Invalid file type. Only JPEG, PNG, and GIF files are allowed.',
        )
        return
      }
      setAvatarFile(file)
      setAvatarError(false) // Reset error state when new file is selected
      const reader = new window.FileReader()
      reader.onload = (ev) => setAvatarUrl(ev.target.result)
      reader.readAsDataURL(file)
      // Enter header edit mode so user can confirm or cancel avatar update
      if (editingSection !== 'header') {
        setEditingSection('header')
        setEditingField(null)
      }
    }
  }

  // Handle avatar image load errors
  const handleAvatarError = () => {
    setAvatarError(true)
  }

  const handlePlayerDataChange = (partialPlayerDataUpdate) => {
    setPlayerData((prevPlayerData) => ({
      ...prevPlayerData, // Preserve existing fields from other cards
      ...partialPlayerDataUpdate, // Apply new changes from the current card
    }))
  }

  const handleSaveAll = async () => {
    if (!userData || !playerData) {
      toastr.error('User or player data is missing.')
      return
    }
    try {
      // Handle avatar upload first if there's a new file
      let finalAvatarUrl = avatarUrl
      if (avatarFile) {
        try {
          console.log('🔵 [CLIENT] Starting avatar upload...')
          console.log(
            '🔵 [CLIENT] File size:',
            avatarFile.size,
            'bytes (',
            (avatarFile.size / 1024 / 1024).toFixed(2),
            'MB )',
          )
          console.log('🔵 [CLIENT] File type:', avatarFile.type)
          console.log('🔵 [CLIENT] File name:', avatarFile.name)

          const uploadResponse = await ApiUsers.uploadAvatar(avatarFile)
          // Backend returns both avatar_url and avatar for compatibility
          finalAvatarUrl = uploadResponse.avatar || uploadResponse.avatar_url

          console.log('🟢 [CLIENT] Avatar upload successful!')
          console.log('🟢 [CLIENT] New avatar URL:', finalAvatarUrl)

          setAvatarUrl(finalAvatarUrl)
          toastr.success('Avatar uploaded successfully!')
        } catch (uploadError) {
          console.error('🔴 [CLIENT] Avatar upload failed:', uploadError)
          toastr.error(`Avatar upload failed: ${uploadError.message}`)
          // Continue with profile update even if avatar upload fails
        }
      }

      // Update user data with the final avatar URL (use 'avatar' to match DB column)
      console.log('🔵 [CLIENT] Updating user data...')
      console.log(
        '🔵 [CLIENT] User data size:',
        JSON.stringify({ ...userData, avatar: finalAvatarUrl }).length,
        'bytes',
      )

      await ApiUsers.update({ ...userData, avatar: finalAvatarUrl })
      const playerUpdate = { ...playerData }
      if ('birthdate' in playerUpdate) {
        playerUpdate.age = calculateAgeFromBirthdate(playerUpdate.birthdate)
      }
      await ApiPlayers.update(playerUpdate)
      setOriginalUserData({ ...userData, avatar: finalAvatarUrl })
      setOriginalPlayerData(playerUpdate)
      toastr.success('Profile updated successfully')
      setEditingSection(null)
      setEditingField(null)
      setAvatarFile(null) // Clear the file after successful upload
    } catch (error) {
      toastr.error(`Error updating profile: ${error.message}`)
    }
  }

  const handleCancel = () => {
    setUserData(originalUserData)
    setPlayerData(originalPlayerData)
    // Restore avatar from original user data (use 'avatar' to match DB column)
    setAvatarUrl(
      originalUserData?.avatar || originalUserData?.avatar_url || null,
    )
    setAvatarFile(null)
    toastr.info('Changes have been canceled.')
    setEditingSection(null)
    setEditingField(null)
  }

  const handleEdit = (section, field = null) => {
    setEditingSection(section)
    setEditingField(field)
  }

  // Inline (per-field) save logic so user doesn't also have to press global save
  const handleInlineSave = async (section, field) => {
    if (!userData || !playerData) return
    try {
      let partialUser = null
      let partialPlayer = null
      if (section === 'tennis') {
        partialPlayer = { [field]: playerData[field] }
        if (field === 'birthdate') {
          partialPlayer.age = calculateAgeFromBirthdate(playerData.birthdate)
        }
      } else if (section === 'contact') {
        // these currently live on playerData in this component context
        partialPlayer = { [field]: playerData[field] }
      } else if (section === 'about') {
        partialPlayer = { [field]: playerData[field] }
      } else if (section === 'header') {
        partialUser = { [field]: userData[field] }
      } else if (section === 'email') {
        partialUser = { [field]: userData[field] }
      }

      if (partialUser) {
        await ApiUsers.update({ ...userData, ...partialUser })
        const merged = { ...userData, ...partialUser }
        setUserData(merged)
        setOriginalUserData(merged)
      }
      if (partialPlayer) {
        await ApiPlayers.update({ ...playerData, ...partialPlayer })
        const mergedP = { ...playerData, ...partialPlayer }
        setPlayerData(mergedP)
        setOriginalPlayerData(mergedP)
      }
      toastr.success('Saved')
    } catch (e) {
      toastr.error(e.message || 'Save failed')
    } finally {
      setEditingSection(null)
      setEditingField(null)
    }
  }

  const handleInlineCancel = (section, field) => {
    // revert only that field from originals
    if (section === 'tennis') {
      setPlayerData((prev) => ({
        ...prev,
        [field]: originalPlayerData?.[field],
      }))
    } else if (section === 'contact' || section === 'about') {
      setPlayerData((prev) => ({
        ...prev,
        [field]: originalPlayerData?.[field],
      }))
    } else if (section === 'header') {
      setUserData((prev) => ({ ...prev, [field]: originalUserData?.[field] }))
    } else if (section === 'email') {
      setUserData((prev) => ({ ...prev, [field]: originalUserData?.[field] }))
    }
    setEditingSection(null)
    setEditingField(null)
  }

  const handleChangePassword = async (e) => {
    e.preventDefault()
    if (!currentPassword || !newPassword || !confirmPassword) {
      toastr.error('Please fill out all password fields.')
      return
    }
    if (newPassword !== confirmPassword) {
      toastr.error('New passwords do not match.')
      return
    }
    setPasswordLoading(true)
    try {
      await ApiUsers.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      })
      toastr.success('Password changed successfully!')
      setShowPasswordForm(false)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err) {
      toastr.error(err.message || 'Failed to change password.')
    } finally {
      setPasswordLoading(false)
    }
  }

  // Centralized ripple utility
  const onRipple = (e) => addRipple(e)

  if (loading || !userData || !playerData) {
    return (
      <PageLayout title="Loading...">
        <div className="modern-profile-loading">
          <div className="loading-spinner"></div>
          <p>Loading your profile...</p>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout title="Profile">
      <div className="modern-profile-modern">
        <style>{`
          .modern-profile-modern {
            max-width: 900px;
            margin: 0 auto;
            padding: var(--spacing-lg);
            display: flex;
            flex-direction: column;
            gap: var(--spacing-lg);
            background: linear-gradient(120deg, #f8fafc 0%, #e3e9f7 100%);
            border-radius: 24px;
            box-shadow: 0 8px 32px rgba(0,0,0,0.10);
            position: relative;
            min-height: 80vh;
          }
          /* Ripple + focus styles for action buttons */
          .ripple-button {
            position: relative;
            overflow: hidden;
            isolation: isolate; /* ensure ripple sits above background */
          }
          .ripple-button:focus-visible {
            outline: none;
            box-shadow: 0 0 0 3px rgba(0,123,255,0.35);
          }
          /* ripple circles are inline styles from util */
          .modern-profile-loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 200px;
            gap: var(--spacing-sm);
          }
          .loading-spinner {
            width: 32px;
            height: 32px;
            border: 3px solid #f3f3f3;
            border-top: 3px solid #007bff;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .profile-avatar-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            margin-bottom: var(--spacing-md);
          }
          .avatar-wrapper { position:relative; display:inline-block; }
          .avatar-wrapper.editing .profile-avatar { box-shadow:0 0 0 4px rgba(0,123,255,0.35); }
          .profile-avatar {
            width: 120px;
            height: 120px;
            border-radius: 50%;
            object-fit: cover;
            box-shadow: 0 2px 12px rgba(0,0,0,0.10);
            border: 4px solid #fff;
            background: #e9ecef;
            margin-bottom: 12px; /* spacing below for layout; overlay is positioned over image */
            transition: box-shadow 0.2s;
          }
          .avatar-overlay { position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:6px; background:rgba(0,0,0,0); color:#fff; font-size:.8rem; font-weight:600; opacity:0; cursor:pointer; border-radius:50%; transition: background .25s, opacity .25s; backdrop-filter: blur(0px); text-decoration:none; }
          .avatar-overlay .camera-icon { font-size:1.4rem; filter:drop-shadow(0 2px 4px rgba(0,0,0,0.4)); }
          .avatar-wrapper:hover .avatar-overlay, .avatar-wrapper:focus-within .avatar-overlay { opacity:1; background:rgba(0,0,0,0.45); backdrop-filter: blur(2px); }
          .avatar-corner { position:absolute; bottom:2px; right:2px; background:#0d6efd; width:30px; height:30px; border-radius:50%; display:flex; align-items:center; justify-content:center; color:#fff; font-size:1rem; box-shadow:0 2px 6px rgba(0,0,0,0.25); pointer-events:none; }
          .avatar-wrapper.editing .avatar-corner { background:#0b5ed7; }
          .avatar-wrapper:focus-within .avatar-overlay { outline:3px solid #1a73e8; outline-offset:2px; }
          .profile-avatar-upload {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }
          .profile-avatar-upload input[type="file"] {
            display: none;
          }
          .profile-avatar-upload label {
            background: #007bff;
            color: #fff;
            padding: 6px 18px;
            border-radius: 20px;
            font-size: 0.95rem;
            cursor: pointer;
            font-weight: 500;
            transition: background 0.2s;
          }
          .profile-avatar-upload label:hover {
            background: #0056b3;
          }
          .profile-actions-modern {
            position: fixed;
            left: 50%;
            transform: translateX(-50%);
            bottom: 20px;
            display: flex;
            justify-content: center;
            gap: 12px;
            padding: 10px 12px;
            background: rgba(255,255,255,0.85);
            backdrop-filter: saturate(180%) blur(8px);
            border: 1px solid rgba(0,0,0,0.06);
            border-radius: 999px;
            box-shadow: 0 10px 24px rgba(0,0,0,0.12);
            z-index: 50;
            pointer-events: none; /* allow clicks through when hidden */
          }
          .profile-actions-modern > * { pointer-events: auto; }
          .profile-actions-modern.hidden { display: none; }
          .profile-actions-modern button {
            padding: 0.7rem 1.6rem;
            border-radius: 20px;
            border: none;
            font-weight: 600;
            font-size: 1.05rem;
            box-shadow: 0 2px 8px rgba(0,0,0,0.04);
            transition: background 0.18s, color 0.18s;
          }
          .profile-actions-modern .save-button {
            background: linear-gradient(90deg, #007bff 60%, #00c6ff 100%);
            color: #fff;
          }
          .profile-actions-modern .save-button:hover {
            background: linear-gradient(90deg, #0056b3 60%, #00aaff 100%);
          }
          .profile-actions-modern .cancel-button {
            background: #f3f3f3;
            color: #333;
          }
          .profile-actions-modern .cancel-button:hover {
            background: #e0e0e0;
          }
          .profile-actions-modern .edit-button {
            background: linear-gradient(90deg, #17a2b8 60%, #43e97b 100%);
            color: #fff;
          }
          .profile-actions-modern .edit-button:hover {
            background: linear-gradient(90deg, #138496 60%, #38d39f 100%);
          }
          .profile-actions-modern .password-button {
            background: linear-gradient(90deg, #ffc107 60%, #ffecb3 100%);
            color: #212529;
          }
          .profile-actions-modern .password-button:hover {
            background: linear-gradient(90deg, #e0a800 60%, #ffe082 100%);
          }
          :root {
            --card-padding: 1.5rem;
            --input-height: 44px;
            --spacing-xs: 0.5rem;
            --spacing-sm: 1rem;
            --spacing-md: 2rem;
            --spacing-lg: 3rem;
            --border-radius: 18px;
            --card-bg: #fff;
            --card-border: 1px solid #e9ecef;
            --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.08);
            --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.10);
          }
          @media (max-width: 600px) {
            .modern-profile-modern {
              padding: var(--spacing-sm);
              border-radius: 0;
              min-height: 100vh;
            }
            .profile-avatar {
              width: 80px;
              height: 80px;
            }
          }
        `}</style>
        <div className="profile-avatar-section">
          <div
            className={`avatar-wrapper ${
              editingSection === 'header' ? 'editing' : ''
            }`}
          >
            <img
              src={avatarError ? DefaultAvatar : avatarUrl || DefaultAvatar}
              alt="Profile avatar"
              className="profile-avatar"
              draggable="false"
              onError={handleAvatarError}
            />
            <input
              id="avatar-upload"
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              style={{ display: 'none' }}
            />
            <label
              htmlFor="avatar-upload"
              className="avatar-overlay"
              aria-label="Change avatar"
            >
              <span className="camera-icon" aria-hidden="true">
                📷
              </span>
              <span className="avatar-text">Change</span>
            </label>
            <div className="avatar-corner" aria-hidden="true">
              📷
            </div>
          </div>
        </div>
        <ProfileHeader
          userData={userData}
          onUserDataChange={handleUserDataChange}
          isEditing={editingSection === 'header'}
          editingField={editingField}
          onEditClick={(field) => handleEdit('header', field)}
        />
        <TennisProfileCard
          playerData={playerData}
          onPlayerDataChange={handlePlayerDataChange}
          isEditing={editingSection === 'tennis'}
          editingField={editingField}
          onEditClick={(field) => handleEdit('tennis', field)}
          onInlineSave={(field) => handleInlineSave('tennis', field)}
          onInlineCancel={(field) => handleInlineCancel('tennis', field)}
        />
        <ContactInfoCard
          playerData={playerData}
          onPlayerDataChange={handlePlayerDataChange}
          isEditing={editingSection === 'contact'}
          editingField={editingField}
          onEditClick={(field) => handleEdit('contact', field)}
          onInlineSave={(field) => handleInlineSave('contact', field)}
          onInlineCancel={(field) => handleInlineCancel('contact', field)}
        />
        <EmailPreferencesCard
          userData={userData}
          onUserDataChange={handleUserDataChange}
          isEditing={editingSection === 'email'}
          editingField={editingField}
          onEditClick={(field) => handleEdit('email', field)}
          onInlineSave={(field) => handleInlineSave('email', field)}
          onInlineCancel={(field) => handleInlineCancel('email', field)}
        />
        <AboutSection
          playerData={playerData}
          onPlayerDataChange={handlePlayerDataChange}
          isEditing={editingSection === 'about'}
          editingField={editingField}
          onEditClick={(field) => handleEdit('about', field)}
          onInlineSave={(field) => handleInlineSave('about', field)}
          onInlineCancel={(field) => handleInlineCancel('about', field)}
        />
        {/* Persistent actions (always visible) */}
        <div
          className="profile-actions-modern"
          style={{
            position: 'static',
            marginTop: 'var(--spacing-md)',
            left: 'auto',
            transform: 'none',
          }}
        >
          <button
            type="button"
            className="ripple-button password-button"
            onMouseDown={onRipple}
            onClick={() => setShowPasswordForm(true)}
          >
            Change Password
          </button>
          {featureFlags.SMS_NOTIFICATIONS && (
            <button
              type="button"
              className="ripple-button edit-button"
              onMouseDown={onRipple}
              onClick={() => (window.location.href = '/notification-settings')}
            >
              <i className="bi bi-bell me-1"></i>
              Notification Settings
            </button>
          )}
        </div>
        {/* Edit mode actions (only visible when editing) */}
        <div
          className={`profile-actions-modern ${
            editingSection === 'header' ? '' : 'hidden'
          }`}
        >
          {editingSection === 'header' ? (
            <>
              <button
                type="button"
                className="ripple-button cancel-button"
                onMouseDown={onRipple}
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ripple-button save-button"
                onMouseDown={onRipple}
                onClick={handleSaveAll}
              >
                Save Changes
              </button>
            </>
          ) : null}
        </div>
        <Modal
          open={showPasswordForm}
          onClose={() => setShowPasswordForm(false)}
        >
          <form
            className="change-password-form"
            onSubmit={handleChangePassword}
            style={{
              width: '100%',
              background: 'transparent',
              border: 'none',
              borderRadius: 0,
              padding: 0,
              boxShadow: 'none',
            }}
          >
            <input
              type="text"
              name="username"
              autoComplete="username"
              value={userData?.email || ''}
              style={{ display: 'none' }}
              readOnly
              tabIndex={-1}
              aria-hidden="true"
            />
            <h5 style={{ marginBottom: 16, textAlign: 'center' }}>
              Change Password
            </h5>
            <div style={{ marginBottom: 12 }}>
              <label htmlFor="current-password-input">Current Password</label>
              <input
                id="current-password-input"
                type="password"
                className="form-control"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label htmlFor="new-password-input">New Password</label>
              <input
                id="new-password-input"
                type="password"
                className="form-control"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="confirm-password-input">
                Confirm New Password
              </label>
              <input
                id="confirm-password-input"
                type="password"
                className="form-control"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
            <div
              style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}
            >
              <button
                type="submit"
                className="btn btn-warning"
                disabled={passwordLoading}
              >
                {passwordLoading ? 'Changing...' : 'Change Password'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowPasswordForm(false)}
                disabled={passwordLoading}
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      </div>
    </PageLayout>
  )
}

// Move Modal outside ModernProfile to avoid remounting issues
function Modal({ open, onClose, children }) {
  if (!open) return null
  const modalBackdropStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: 'rgba(0,0,0,0.38)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: 'fadeIn 0.2s',
  }
  const modalContentStyle = {
    background: 'linear-gradient(120deg, #fff 80%, #e3e9f7 100%)',
    borderRadius: 18,
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    minWidth: 320,
    maxWidth: 400,
    width: '100%',
    padding: 28,
    position: 'relative',
    animation: 'slideUp 0.22s',
  }
  return (
    <div style={modalBackdropStyle}>
      <div style={modalContentStyle}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            background: 'none',
            border: 'none',
            fontSize: 26,
            color: '#888',
            cursor: 'pointer',
            fontWeight: 700,
          }}
          aria-label="Close"
        >
          ×
        </button>
        {children}
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { transform: translateY(40px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
        `}</style>
      </div>
    </div>
  )
}

export default ModernProfile
